import { supabase } from './supabase';
import type { PackageSku } from './offerte-constants';
import type { AppointmentType, RouteOption } from './appointments';
import type { DiscountReason } from './discount-constants';

export interface OfferteProduct {
  id: string;
  productCode: string;
  productName: string;
  quantity: number;
  lengthCm: number | null;
  widthCm: number | null;
  diameterCm: number | null;
  oppervlakteM2: number;
  verkoopprijs: number;
  totalePrijs: number;
  btwPercentage: number;
  btwBedrag: number;
  totaalInclBtw: number;          // netto incl. (ná korting)
  totaalInclBtwBruto?: number;    // bruto incl. (vol tarief, vóór korting) — getoond per regel
  bijzonderheden: string;
  externeReferentie?: string;
  // Parent-child koppeling voor nested rendering van behandelingen onder kleden
  parentLineItemId?: string | null;
  relatievePrijs?: number | null;
  itemType?: 'kleed' | 'behandeling' | 'toeslag';
}

export interface OffertePackage {
  sku: PackageSku;
  label: string;
  description: string;
  features: string[];
  priceExclBtw: number;
  priceInclBtw: number;
  priceDifference: number;
  isPopular: boolean;
  sortOrder: number;
}

export interface OfferteCustomer {
  firstName: string;
  lastName: string;
  name: string;
  company?: string;        // alleen gevuld bij echte B2B-bedrijfsnaam (placeholder onderdrukt)
  email: string;
  phone: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  postcodegebied: string;
}

export interface OfferteAddress {
  street: string; // straat + huisnummer
  postalCode: string;
  city: string;
  toevoeging: string;
}

/**
 * Ophaal- en (optioneel afwijkend) afleveradres op de offerte/lead. Het
 * ophaaladres stuurt het route-gebied aan dat de klant bij acceptatie ziet —
 * bij B2B wijkt dit af van het bedrijfs-/factuuradres. Leeg ophaaladres = valt
 * terug op het klantadres (Street/PostalCode/City).
 */
export interface OfferteServiceAddress {
  pickup: OfferteAddress;
  delivery: OfferteAddress;
  useAlternateDelivery: boolean;
}

export interface OfferteTotals {
  subTotaal: number;           // netto excl. BTW (ná korting)
  subTotaalBruto?: number;     // bruto excl. BTW (vóór korting)
  kortingBedrag?: number;      // totale korting excl. BTW (0 = geen korting)
  kortingPercentage?: number | null; // effectief korting-%
  btwTotaal: number;
  eindTotaal: number;
  minimumOrderExclBtw: number | null;
  minimumOrderInclBtw: number | null;
}

/**
 * Korting op de offerte (Lead.Actie_Code__c), parallel aan OrderDiscount op de
 * order-kant:
 *  - kind='manual' → ad-hoc 'KORTING:X' — wijzigbaar in office-app
 *  - kind='campaign' → geregistreerde campagne-code (bv. 'lente2026') — read-only
 *  - kind='none' → geen korting
 */
export type OfferteDiscount =
  | { kind: 'none'; percentage: null; code: null; totalAmountInclBtw: number }
  | { kind: 'manual'; percentage: number | null; code: string; totalAmountInclBtw: number }
  | { kind: 'toeslag'; percentage: number | null; code: string; totalAmountInclBtw: number }
  | { kind: 'campaign'; percentage: null; code: string; totalAmountInclBtw: number };

export interface OfferteDetail {
  leadId: string;
  leadnummer: string;
  status: string;
  isAlreadyAccepted: boolean;
  brand: string;
  createdDate: string;
  lastModifiedDate: string;
  customer: OfferteCustomer;
  serviceAddress: OfferteServiceAddress;
  products: OfferteProduct[];
  packages: OffertePackage[];
  currentPackageSku: PackageSku;
  totals: OfferteTotals;
  hasSignature: boolean;
  /** Klant-facing offertelink (offerte.kleda.nl). Leeg als er nog geen link is. */
  offerPublicUrl?: string;
  discount?: OfferteDiscount;
  /** Automatische bundelkorting uitgesloten (B2B opt-out). */
  geenBundelkorting?: boolean;
}

export interface OfferteListItem {
  id: string;
  leadnummer: string;
  name: string;
  email: string;
  city: string;
  postcode: string;
  status: string;
  totalBasis: number;
  createdDate: string;
  lastModifiedDate: string;
}

// Single canoniek route-option type — alias van RouteOption uit appointments.ts.
// Voorkomt drift tussen orders- en offertes-route-shape; route-lookup loopt via
// dezelfde Apex (OfficeAppointmentService) en levert dezelfde DTO.
export type OfferteRouteOption = RouteOption;

export interface AcceptOfferteRequest {
  packageSku: PackageSku;
  phone?: string;
  pickupStreet?: string;
  pickupHouseNumber?: string;
  pickupHouseNumberAddition?: string;
  pickupPostalCode?: string;
  pickupCity?: string;
  useAlternateDelivery?: boolean;
  deliveryStreet?: string;
  deliveryHouseNumber?: string;
  deliveryHouseNumberAddition?: string;
  deliveryPostalCode?: string;
  deliveryCity?: string;
  petPackageReason?: string;
  selectedRouteId?: string | null;
  notes?: string;
  /** Bevestigingen-toggle (S01/S03). False = uitgesteld; klant krijgt geen mail tot kantoor activeert. */
  bevestigingenSturen?: boolean;
}

export interface AcceptOfferteResponse {
  success: boolean;
  message: string;
  newStatus?: string;
  leadnummer?: string;
  hasScheduledRoute?: boolean;
  scheduledRouteLabel?: string;
  workOrderId?: string;
  workOrderNumber?: string;
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
  return h;
}

async function parseOrThrow<T = unknown>(response: Response): Promise<T> {
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    const error = new Error(err.error || err.message || 'Request failed') as Error & {
      status?: number;
      code?: string;
      hasSignature?: boolean;
      workOrderId?: string;
    };
    error.status = response.status;
    if (typeof err.code === 'string') error.code = err.code;
    if (typeof err.hasSignature === 'boolean') error.hasSignature = err.hasSignature;
    if (typeof err.workOrderId === 'string') error.workOrderId = err.workOrderId;
    throw error;
  }
  return response.json() as Promise<T>;
}

export type OfferteListView = 'open' | 'wacht';

export async function fetchOfferteList(view: OfferteListView = 'open'): Promise<OfferteListItem[]> {
  const headers = await authHeaders();
  const qs = view === 'wacht' ? '?view=wacht' : '';
  const response = await fetch(`/api/offertes/list${qs}`, { headers });
  const payload = await parseOrThrow<{ offertes?: OfferteListItem[] }>(response);
  return Array.isArray(payload.offertes) ? payload.offertes : [];
}

export async function searchOffertes(
  query: string,
  signal?: AbortSignal
): Promise<OfferteListItem[]> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/offertes/search?q=${encodeURIComponent(query)}`,
    { headers, signal }
  );
  const payload = await parseOrThrow<{ offertes?: OfferteListItem[] }>(response);
  return Array.isArray(payload.offertes) ? payload.offertes : [];
}

export async function fetchOfferteDetail(id: string): Promise<OfferteDetail> {
  const headers = await authHeaders();
  const response = await fetch(`/api/offertes/detail?id=${encodeURIComponent(id)}`, { headers });
  return parseOrThrow<OfferteDetail>(response);
}

export async function fetchOfferteRoutes(
  postcode: string,
  options?: { includeFull?: boolean; officeMode?: boolean; type?: AppointmentType }
): Promise<OfferteRouteOption[]> {
  const headers = await authHeaders();
  const qs = new URLSearchParams({
    postcode,
    type: options?.type ?? 'pickup',
  });
  // Office-app default: officeMode + includeFull aan zodat de picker dezelfde
  // "Vol" / "Chauffeur onderweg" / "Niet voorstellen" badges + Capaciteit+
  // mogelijkheid laat zien als de orders-flow.
  const includeFull = options?.includeFull ?? true;
  const officeMode = options?.officeMode ?? true;
  if (includeFull) qs.set('includeFull', '1');
  if (officeMode) qs.set('officeMode', '1');
  const response = await fetch(`/api/offertes/routes?${qs.toString()}`, { headers });
  const payload = await parseOrThrow<{ routes?: OfferteRouteOption[] }>(response);
  return Array.isArray(payload.routes) ? payload.routes : [];
}

export interface BumpOfferteCapacityResponse {
  ok: boolean;
  newRemainingCapacity: number | null;
}

export async function bumpOfferteRouteCapacity(
  leadId: string,
  routeId: string,
  type: AppointmentType,
  delta: number,
  reason: string
): Promise<BumpOfferteCapacityResponse> {
  const headers = await authHeaders();
  const response = await fetch('/api/routes/capacity', {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ leadId, routeId, type, delta, reason }),
  });
  const payload = await parseOrThrow<{ ok?: boolean; newRemainingCapacity?: number | null }>(response);
  return {
    ok: payload.ok === true,
    newRemainingCapacity: typeof payload.newRemainingCapacity === 'number'
      ? payload.newRemainingCapacity
      : null,
  };
}

export async function acceptOfferte(
  leadId: string,
  req: AcceptOfferteRequest
): Promise<AcceptOfferteResponse> {
  const headers = await authHeaders();
  const response = await fetch('/api/offertes/accepteer', {
    method: 'POST',
    headers,
    body: JSON.stringify({ leadId, ...req }),
  });
  return parseOrThrow<AcceptOfferteResponse>(response);
}

export async function resendOfferte(leadId: string): Promise<{ ok: boolean; message: string }> {
  const headers = await authHeaders();
  const response = await fetch('/api/offertes/resend', {
    method: 'POST',
    headers,
    body: JSON.stringify({ leadId }),
  });
  return parseOrThrow<{ ok: boolean; message: string }>(response);
}

export interface UpdateLeadPatch {
  Name?: string;
  Email?: string;
  Phone?: string;
  Street?: string;
  PostalCode?: string;
  City?: string;
}

export interface UpdateLeadResponse {
  success: boolean;
  updatedFields: string[];
  lastModifiedDate: string | null;
  postcodegebiedId: string | null;
  postcodegebiedNaam: string | null;
}

export async function updateOfferteCustomer(
  leadId: string,
  patch: UpdateLeadPatch
): Promise<UpdateLeadResponse> {
  const headers = await authHeaders();
  const response = await fetch('/api/leads/update', {
    method: 'POST',
    headers,
    body: JSON.stringify({ leadId, patch }),
  });
  return parseOrThrow<UpdateLeadResponse>(response);
}

export interface ServiceAddressSide {
  street: string; // straat + huisnummer
  postalCode: string;
  city: string;
  toevoeging?: string;
}

/**
 * Schrijft ophaal- en (optioneel afwijkend) afleveradres naar de lead via
 * /api/leads/update. Het route-gebied (Postcodegebied__c) volgt server-side de
 * ophaalpostcode, zodat de klant bij acceptatie de juiste route-data ziet.
 *  - pickup=null  → ophaaladres wissen (route-gebied valt terug op klantadres)
 *  - delivery=null → geen afwijkend afleveradres (Ander_afleveradres__c=false)
 * De response bevat het nieuwe postcodegebied zodat de UI direct kan tonen welk
 * gebied nu geldt.
 */
export async function updateOfferteServiceAddress(
  leadId: string,
  input: { pickup: ServiceAddressSide | null; delivery: ServiceAddressSide | null }
): Promise<UpdateLeadResponse> {
  const patch: Record<string, unknown> = {};

  if (input.pickup) {
    patch.Ophaalstraat__c = input.pickup.street;
    patch.Ophaalpostcode__c = input.pickup.postalCode;
    patch.Ophaalplaats__c = input.pickup.city;
    patch.Ophaaladres_Toevoeging__c = input.pickup.toevoeging ?? '';
  } else {
    patch.Ophaalstraat__c = '';
    patch.Ophaalpostcode__c = '';
    patch.Ophaalplaats__c = '';
    patch.Ophaaladres_Toevoeging__c = '';
  }

  if (input.delivery) {
    patch.Ander_afleveradres__c = true;
    patch.Afleverstraat__c = input.delivery.street;
    patch.Afleverpostcode__c = input.delivery.postalCode;
    patch.Afleverplaats__c = input.delivery.city;
    patch.Afleveradres_Toevoeging__c = input.delivery.toevoeging ?? '';
  } else {
    patch.Ander_afleveradres__c = false;
    patch.Afleverstraat__c = '';
    patch.Afleverpostcode__c = '';
    patch.Afleverplaats__c = '';
    patch.Afleveradres_Toevoeging__c = '';
  }

  const headers = await authHeaders();
  const response = await fetch('/api/leads/update', {
    method: 'POST',
    headers,
    body: JSON.stringify({ leadId, patch }),
  });
  return parseOrThrow<UpdateLeadResponse>(response);
}

/**
 * Zet Lead.Status (en optioneel de reden) via de Brein-klare, geaudite endpoint
 * /api/offertes/set-status. Eén write-pad voor offerte-/lead-status.
 */
export async function setOfferteStatus(
  leadId: string,
  status: string,
  reason?: string
): Promise<{ ok: boolean; status?: string }> {
  const headers = await authHeaders();
  const response = await fetch('/api/offertes/set-status', {
    method: 'POST',
    headers,
    body: JSON.stringify({ leadId, status, ...(reason !== undefined ? { reason } : {}) }),
  });
  return parseOrThrow<{ ok: boolean; status?: string }>(response);
}

export interface TerugkomStand {
  offerteId: string;
  leadNummer: string | null;
  terugkomOp: string | null;
  terugkomContext: string | null;
  /** In de woorden van de klant ("na je vakantie in november"); openingszin van de herinneringsmail. */
  terugkomRedenKlant: string | null;
  terugkomGezetAt: string | null;
  terugkomVerstuurdAt: string | null;
}

/** Stand van het terugkom-moment op de offerte (api/offertes/terugkom, GET). */
export async function fetchTerugkomMoment(leadId: string): Promise<TerugkomStand | null> {
  const headers = await authHeaders();
  const response = await fetch(`/api/offertes/terugkom?leadId=${encodeURIComponent(leadId)}`, { headers });
  if (response.status === 404) return null;
  return parseOrThrow<TerugkomStand>(response);
}

/**
 * Terugkom-moment zetten (YYYY-MM-DD) of wissen (null). De cron lead-terugkom
 * mailt die dag de offerte opnieuw met actuele prijs; zet niet de status.
 */
export async function setTerugkomMoment(
  leadId: string,
  terugkomOp: string | null,
  context?: string | null,
  redenKlant?: string | null,
): Promise<{ ok: boolean; terugkomOp: string | null }> {
  const headers = await authHeaders();
  const response = await fetch('/api/offertes/terugkom', {
    method: 'POST',
    headers,
    body: JSON.stringify({ leadId, terugkomOp, context: context ?? null, redenKlant: redenKlant ?? null }),
  });
  return parseOrThrow<{ ok: boolean; terugkomOp: string | null }>(response);
}

export interface SetOfferteDiscountResult {
  appliedPercentage: number;
  nieuweTotaalInclBtw: number | null;
  nieuweLastModifiedDate: string | null;
  noChanges: boolean;
}

/**
 * Stel een handmatige offerte-korting in via Lead.Actie_Code__c='KORTING:<pct>'.
 * Geef `percentage=null` of `0` om de korting te verwijderen. Met
 * `overrideCampaign=true` mag een geregistreerde campagne-code (bv. `lente2026`)
 * worden vervangen. PricingService herprijst de offerte automatisch; bij akkoord
 * loopt de korting mee naar de order.
 */
export async function setOfferteDiscount(
  leadId: string,
  percentage: number | null,
  options?: {
    overrideCampaign?: boolean;
    type?: 'korting' | 'toeslag';
    /** Verplicht bij toepassen (percentage > 0), optioneel bij verwijderen. */
    reason?: DiscountReason;
    reasonNote?: string;
  }
): Promise<SetOfferteDiscountResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/offertes/discount', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      leadId,
      percentage,
      overrideCampaign: options?.overrideCampaign === true,
      ...(options?.type ? { type: options.type } : {}),
      ...(options?.reason ? { reason: options.reason } : {}),
      ...(options?.reasonNote ? { reasonNote: options.reasonNote } : {}),
    }),
  });
  const payload = await parseOrThrow<{
    appliedPercentage?: number;
    nieuweTotaalInclBtw?: number | null;
    nieuweLastModifiedDate?: string | null;
    noChanges?: boolean;
  }>(response);
  return {
    appliedPercentage: payload.appliedPercentage ?? 0,
    nieuweTotaalInclBtw: payload.nieuweTotaalInclBtw ?? null,
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    noChanges: !!payload.noChanges,
  };
}
